import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
} from '@/components/ui/form-control-styles'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { DatePicker } from '@/components/shared/DatePicker'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import { TaskChecklistSection } from '@/components/tasks/TaskChecklistSection'
import { TaskComments } from '@/components/tasks/TaskComments'
import type { AttachmentRef } from '@/components/shared/types'
import { DOCUMENT_FILE_ACCEPT, isDocumentFileExtension } from '@/lib/constants'
import { renameLocalFile } from '@/lib/documentRename'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import { cn } from '@/lib/utils'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { isForbiddenError } from '@/lib/tasks'
import { tasksApi } from '@/services/tasks'
import { usersApi } from '@/services/users'
import { clientsApi } from '@/services/clients'
import { quotesApi } from '@/services/quotes'
import { invoicesApi } from '@/services/invoices'
import { contractsApi } from '@/services/contracts'
import { jobsApi } from '@/services/jobs'
import { useCreateTask, useUpdateTask, useTask } from '@/hooks/useTasks'
import { useAppSelector } from '@/store/hooks'
import {
  TASK_RELATED_TYPES,
  normalizeRelatedType,
  taskRelatedEntityLabel,
  type CreateTaskRequest,
  type TaskPriority,
} from '@/types'

const PRIORITY_OPTIONS: { value: TaskPriority; labelKey: string }[] = [
  { value: 'urgent', labelKey: 'priorityOptions.urgent' },
  { value: 'high', labelKey: 'priorityOptions.high' },
  { value: 'medium', labelKey: 'priorityOptions.medium' },
  { value: 'low', labelKey: 'priorityOptions.low' },
]

const PRIORITY_META: Record<
  TaskPriority,
  { dot: string; selectedBg: string; selectedBorder: string }
> = {
  urgent: {
    dot: '#d63b33',
    selectedBg: '#fde9e8',
    selectedBorder: '#f8b4b4',
  },
  high: {
    dot: '#efac00',
    selectedBg: '#fff7e6',
    selectedBorder: '#ffdc80',
  },
  medium: {
    dot: '#3f81ea',
    selectedBg: '#eef4ff',
    selectedBorder: '#c0d4f5',
  },
  low: {
    dot: '#303030',
    selectedBg: '#f4f5f8',
    selectedBorder: '#d9d9d9',
  },
}

const NONE = '__none__'

const fieldInputClass =
  'h-10 rounded-xl border-transparent bg-[#f4f5f8] px-3 py-3 text-sm font-normal placeholder:font-normal shadow-none'

const selectTriggerClass =
  'h-10 rounded-xl border-transparent bg-[#f4f5f8] px-3 text-sm font-normal placeholder:font-normal data-[placeholder]:font-normal [&>span]:font-normal [&>span>span]:font-normal'

const entityPickerClass = cn(
  'h-10 rounded-xl border-transparent bg-[#f4f5f8] px-3 py-3 text-sm font-normal shadow-none hover:bg-[#eef0f5] [&>span]:font-normal',
  FORM_CONTROL_FOCUS_CLASS
)

const textareaFieldClass =
  'min-h-[88px] rounded-xl border-transparent bg-[#f4f5f8] px-3 py-2 text-sm font-normal placeholder:font-normal shadow-none'

const spaciousTextareaFieldClass =
  'min-h-[88px] rounded-xl border-transparent bg-[#f4f5f8] px-4 py-4 text-sm font-normal placeholder:font-normal shadow-none'

const dateFieldClass = cn(
  fieldInputClass,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  '[&_input]:text-sm [&_input]:font-normal'
)

const outlineBtnClass =
  'h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-medium text-[#272c36]'

const primaryBtnClass = 'h-10 rounded-xl px-4 text-sm font-medium text-white'

type DraftChecklistItem = {
  id: string
  label: string
}

type CreateTaskFollowUps = {
  internalNote?: string
  checklistLabels: string[]
  files: File[]
}

interface Errors {
  title?: string
  assigned_to?: string
  priority?: string
  due_date?: string
  attachments?: string
}

function makeLocalId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function NumberBadge({ value }: { value: number }) {
  return (
    <span className='inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary text-xs font-medium text-white'>
      {value}
    </span>
  )
}

function SectionCard({
  index,
  title,
  children,
}: {
  index: number
  title: string
  children: ReactNode
}) {
  return (
    <section className='rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_3px_rgba(23,26,32,0.04)]'>
      <div className='flex items-center gap-2 px-4 py-4'>
        <NumberBadge value={index} />
        <h2 className='font-heading text-sm font-semibold leading-[1.2] tracking-normal text-[#171a20]'>
          {title}
        </h2>
      </div>
      <div className='space-y-3 px-4 pb-4'>{children}</div>
    </section>
  )
}

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='text-xs font-medium leading-4 text-[#171a20]'
    >
      {children}
      {required && <span className='text-destructive'> *</span>}
    </Label>
  )
}

function PriorityChip({
  label,
  selected,
  priority,
  onClick,
}: {
  label: string
  selected: boolean
  priority: TaskPriority
  onClick: () => void
}) {
  const meta = PRIORITY_META[priority]
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-medium transition-colors',
        selected
          ? 'text-[#272c36]'
          : 'border-[#d9d9d9] bg-white text-[#272c36] hover:bg-[#f4f5f8]'
      )}
      style={
        selected
          ? {
              backgroundColor: meta.selectedBg,
              borderColor: meta.selectedBorder,
            }
          : undefined
      }
    >
      <span
        className='size-1.5 rounded-full'
        style={{ backgroundColor: meta.dot }}
      />
      {label}
    </button>
  )
}

function SubtaskEditor({
  items,
  disabled,
  onAdd,
  onChange,
  onRemove,
  addLabel,
  itemLabel,
  placeholder,
  removeLabel,
}: {
  items: DraftChecklistItem[]
  disabled?: boolean
  onAdd: () => void
  onChange: (id: string, value: string) => void
  onRemove: (id: string) => void
  addLabel: string
  itemLabel: string
  placeholder: string
  removeLabel: string
}) {
  return (
    <div className='space-y-3'>
      {items.map(item => (
        <div key={item.id} className='space-y-1.5'>
          <FieldLabel>{itemLabel}</FieldLabel>
          <div className='flex items-center gap-2'>
            <Input
              value={item.label}
              maxLength={255}
              placeholder={placeholder}
              disabled={disabled}
              onChange={event => onChange(item.id, event.target.value)}
              className={fieldInputClass}
            />
            <button
              type='button'
              disabled={disabled}
              onClick={() => onRemove(item.id)}
              className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#e7e9ef] bg-white text-[#757575] hover:bg-[#f4f5f8] disabled:opacity-50'
              aria-label={removeLabel}
            >
              <X className='h-4 w-4' />
            </button>
          </div>
        </div>
      ))}

      <Button
        type='button'
        variant='outline'
        className='h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-medium text-[#272c36]'
        disabled={disabled}
        onClick={onAdd}
      >
        <Plus className='mr-1.5 h-4 w-4' />
        {addLabel}
      </Button>
    </div>
  )
}

function RequiredLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <FieldLabel htmlFor={htmlFor} required>
      {children}
    </FieldLabel>
  )
}

export default function TaskFormPage({
  mode,
  embedded = false,
  onCancelOverride,
}: {
  mode: 'create' | 'edit'
  embedded?: boolean
  onCancelOverride?: () => void
}) {
  const { t } = useTranslation('tasks')
  const { t: tShared } = useTranslation('shared')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const isEdit = mode === 'edit'

  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useTask(isEdit ? id : undefined)
  const create = useCreateTask()
  const update = useUpdateTask(id ?? '')
  const viewer = useAppSelector(s => s.auth.user)
  const assigneeLocked = viewer?.role === 'Operator'
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  )

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [assignedTo, setAssignedTo] = useState<string | null>(null)
  const [assigneeLabel, setAssigneeLabel] = useState<string>()
  const [priority, setPriority] = useState<TaskPriority | ''>('')
  const [dueDate, setDueDate] = useState('')
  const [relatedType, setRelatedType] = useState<string>('')
  const [relatedId, setRelatedId] = useState<string | null>(null)
  const [relatedEntityLabel, setRelatedEntityLabel] = useState<string>()
  const [errors, setErrors] = useState<Errors>({})
  const [checklist, setChecklist] = useState<DraftChecklistItem[]>([
    { id: makeLocalId(), label: '' },
  ])
  const [attachments, setAttachments] = useState<AttachmentRef[]>([])
  const [isAttachmentDragActive, setIsAttachmentDragActive] = useState(false)
  const hydratedAttachmentsForRef = useRef<string | null>(null)

  useEffect(() => {
    if (isEdit || !assigneeLocked || !viewer?.id) return
    /* eslint-disable react-hooks/set-state-in-effect */
    setAssignedTo(viewer.id)
    setAssigneeLabel(viewer.full_name ?? viewer.email ?? viewer.id)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [isEdit, assigneeLocked, viewer])

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!isEdit || !detail?.data) return
    const task = detail.data
    setTitle(task.title)
    setDescription(task.description ?? '')
    setInternalNotes('')
    if (assigneeLocked && viewer?.id) {
      setAssignedTo(viewer.id)
      setAssigneeLabel(viewer.full_name ?? viewer.email ?? viewer.id)
    } else {
      setAssignedTo(task.assigned_to)
      setAssigneeLabel(
        task.assignee
          ? (task.assignee.full_name ?? task.assignee.email ?? task.assignee.id)
          : undefined
      )
    }
    setPriority(task.priority)
    setDueDate(task.due_date ? task.due_date.slice(0, 10) : '')
    setRelatedType(normalizeRelatedType(task.related_type))
    setRelatedId(task.related_id)
    setRelatedEntityLabel(undefined)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [isEdit, detail, assigneeLocked, viewer])

  useEffect(() => {
    if (!isEdit || !id || hydratedAttachmentsForRef.current === id) return

    let active = true
    tasksApi
      .listFiles(id)
      .then(response => {
        if (!active) return
        hydratedAttachmentsForRef.current = id
        const persisted = (response.data ?? []).map<AttachmentRef>(file => ({
          id: file.id,
          name: file.name ?? file.id,
          url: file.url,
          mime_type: file.mime_type,
          size: file.size,
          source: 'upload',
          created_at: file.created_at,
        }))
        setAttachments(current => [
          ...persisted,
          ...current.filter(attachment => !!attachment.file),
        ])
      })
      .catch(error => {
        if (!active) return
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(error, t('attachments.loadError')),
        })
      })

    return () => {
      active = false
    }
  }, [id, isEdit, t])

  const resolvedRelatedEntityLabel =
    relatedType && relatedId ? relatedEntityLabel : undefined

  useEffect(() => {
    if (!relatedType || !relatedId) return

    let cancelled = false
    const loadLabel = async () => {
      try {
        let item:
          | {
              id: string
              number?: string | null
              name?: string | null
              reference?: string | null
              full_name?: string | null
            }
          | null
          | undefined
        switch (relatedType) {
          case 'client':
            item = (await clientsApi.get(relatedId)).data
            break
          case 'quote':
            item = (await quotesApi.get(relatedId)).data
            break
          case 'invoice':
            item = (await invoicesApi.get(relatedId)).data
            break
          case 'contract':
            item = (await contractsApi.get(relatedId)).data
            break
          case 'job':
            item = (await jobsApi.get(relatedId)).data
            break
          default:
            item = null
        }
        if (!cancelled && item) {
          setRelatedEntityLabel(taskRelatedEntityLabel(relatedType, item))
        }
      } catch {
        if (!cancelled) setRelatedEntityLabel(undefined)
      }
    }

    loadLabel()
    return () => {
      cancelled = true
    }
  }, [relatedType, relatedId])

  const entityFetcher = useMemo(() => {
    const f =
      (
        fn: (p: {
          search?: string
          limit?: number
          page?: number
        }) => Promise<unknown>
      ) =>
      (p: { search: string; limit: number; page: number }) =>
        fn(p) as never
    switch (relatedType) {
      case 'client':
        return f(p => clientsApi.list(p))
      case 'quote':
        return f(p => quotesApi.list(p))
      case 'invoice':
        return f(p => invoicesApi.list(p))
      case 'contract':
        return f(p => contractsApi.list(p))
      case 'job':
        return f(p => jobsApi.list(p))
      default:
        return null
    }
  }, [relatedType])

  const getRelatedOptionLabel = useMemo(
    () =>
      (item: {
        id: string
        number?: string | null
        name?: string | null
        reference?: string | null
        full_name?: string | null
      }) =>
        taskRelatedEntityLabel(relatedType, item),
    [relatedType]
  )

  const validate = () => {
    const next: Errors = {}
    if (!title.trim()) next.title = t('form.validation.title')
    if (!assignedTo) next.assigned_to = t('form.validation.assignedTo')
    if (!priority) next.priority = t('form.validation.priority')
    if (!dueDate) next.due_date = t('form.validation.dueDate')
    setErrors(next)
    return next
  }

  const pickFiles = (files: FileList | null) => {
    if (!files?.length) return

    let attachmentError: string | undefined
    const nextRefs: AttachmentRef[] = []

    Array.from(files).forEach(file => {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!isDocumentFileExtension(extension)) {
        attachmentError = t('attachments.unsupportedFormat')
        return
      }

      if (isFileTooLarge(file)) {
        attachmentError = tShared('attachFiles.tooLarge', {
          max: getFileSizeLimitMb(),
        })
        return
      }

      const exists = attachments.some(
        item =>
          item.file?.name === file.name &&
          item.file.size === file.size &&
          item.file.lastModified === file.lastModified
      )
      if (exists) return

      nextRefs.push({
        id: makeLocalId(),
        name: file.name,
        size: file.size,
        mime_type: file.type,
        source: 'upload',
        file,
        created_at: new Date(file.lastModified).toISOString(),
      })
    })

    if (attachmentError) {
      setErrors(prev => ({ ...prev, attachments: attachmentError }))
    }

    if (nextRefs.length > 0) {
      setAttachments(prev => [...prev, ...nextRefs])
      if (!attachmentError) {
        setErrors(prev => ({ ...prev, attachments: undefined }))
      }
    }
  }

  const removeAttachment = async (item: AttachmentRef) => {
    if (item.file || !isEdit || !id || !item.id) {
      setAttachments(prev => prev.filter(file => file !== item))
      return
    }

    try {
      await tasksApi.removeFile(id, item.id)
      setAttachments(prev => prev.filter(file => file.id !== item.id))
      queryClient.invalidateQueries({ queryKey: ['tasks', 'files', id] })
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', id] })
      toast({ variant: 'success', title: t('attachments.removeSuccess') })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('attachments.removeError')),
      })
    }
  }

  const renameAttachment = async (item: AttachmentRef, name: string) => {
    if (!item.id) return
    if (item.file) {
      const file = renameLocalFile(item.file, name)
      setAttachments(current =>
        current.map(attachment =>
          attachment.id === item.id
            ? {
                ...attachment,
                name: file.name,
                size: file.size,
                mime_type: file.type,
                file,
              }
            : attachment
        )
      )
      return
    }

    if (!isEdit || !id) return
    const response = await tasksApi.renameFile(id, item.id, name)
    setAttachments(current =>
      current.map(attachment =>
        attachment.id === item.id
          ? {
              ...attachment,
              name: response.data?.name ?? attachment.name,
              url: response.data?.url ?? attachment.url,
            }
          : attachment
      )
    )
    queryClient.invalidateQueries({ queryKey: ['tasks', 'files', id] })
    queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', id] })
    toast({ variant: 'success', title: t('attachments.renameSuccess') })
  }

  const addSubtask = () => {
    setChecklist(prev => [...prev, { id: makeLocalId(), label: '' }])
  }

  const updateSubtask = (targetId: string, value: string) => {
    setChecklist(prev =>
      prev.map(item =>
        item.id === targetId ? { ...item, label: value } : item
      )
    )
  }

  const removeSubtask = (targetId: string) => {
    setChecklist(prev => {
      const next = prev.filter(item => item.id !== targetId)
      return next.length > 0 ? next : [{ id: makeLocalId(), label: '' }]
    })
  }

  const onSubmit = async () => {
    const nextErrors = validate()
    if (Object.keys(nextErrors).length > 0) {
      const firstError = Object.values(nextErrors)[0]
      if (firstError) {
        toast({ variant: 'destructive', title: firstError })
      }
      return
    }

    const body: CreateTaskRequest = {
      title: title.trim(),
      description: description.trim() || undefined,
      assigned_to:
        assigneeLocked && viewer?.id ? viewer.id : (assignedTo ?? undefined),
      priority: (priority || undefined) as TaskPriority | undefined,
      due_date: dueDate || undefined,
      related_type: relatedType || undefined,
      related_id: relatedId ?? undefined,
    }
    const followUps: CreateTaskFollowUps = {
      internalNote: internalNotes.trim() || undefined,
      checklistLabels: checklist.map(item => item.label.trim()).filter(Boolean),
      files: attachments.flatMap(attachment =>
        attachment.file ? [attachment.file] : []
      ),
    }

    try {
      if (isEdit) {
        const res = await update.mutateAsync(body)
        const taskId = res.data?.id ?? id
        if (taskId && followUps.files.length > 0) {
          const results = await Promise.allSettled(
            followUps.files.map(file => tasksApi.uploadFile(taskId, file))
          )
          queryClient.invalidateQueries({
            queryKey: ['tasks', 'files', taskId],
          })
          queryClient.invalidateQueries({
            queryKey: ['tasks', 'detail', taskId],
          })
          if (results.some(result => result.status === 'rejected')) {
            toast({
              variant: 'destructive',
              title: t('form.updateFollowUpError'),
            })
          }
        }
        toast({ variant: 'success', title: t('form.updateSuccess') })
        navigate(`/tasks/${taskId}`)
        return
      }

      const res = await create.mutateAsync(body)
      const newId = res.data?.id

      if (newId) {
        toast({ variant: 'success', title: t('form.createSuccess') })
        navigate(`/tasks/${newId}`)
        void persistCreateFollowUps(newId, followUps)
        return
      }

      toast({ variant: 'success', title: t('form.createSuccess') })
      navigate('/tasks')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          isEdit ? t('form.updateError') : t('form.createError')
        ),
      })
    }
  }

  const persistCreateFollowUps = async (
    taskId: string,
    followUps: CreateTaskFollowUps
  ) => {
    const operations: Promise<unknown>[] = []

    if (followUps.internalNote) {
      operations.push(
        tasksApi.addComment(taskId, { body: followUps.internalNote })
      )
    }

    followUps.checklistLabels.forEach(label => {
      operations.push(
        tasksApi.addChecklistItem(taskId, {
          label,
          is_done: false,
        })
      )
    })

    followUps.files.forEach(file => {
      operations.push(tasksApi.uploadFile(taskId, file))
    })

    if (operations.length === 0) return

    const results = await Promise.allSettled(operations)
    queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
    queryClient.invalidateQueries({ queryKey: ['tasks', 'checklist', taskId] })
    queryClient.invalidateQueries({ queryKey: ['tasks', 'comments', taskId] })
    queryClient.invalidateQueries({ queryKey: ['tasks', 'files', taskId] })

    if (results.some(result => result.status === 'rejected')) {
      toast({
        variant: 'destructive',
        title: t('form.createFollowUpError'),
      })
    }
  }

  const onCancel =
    onCancelOverride ??
    (() => {
      navigate(isEdit && id ? `/tasks/${id}` : '/tasks')
    })

  const pageTitle = isEdit ? t('form.editTitle') : t('form.createTitle')
  const pending = create.isPending || update.isPending

  if (isEdit && isLoading) {
    return (
      <div className='-mx-6 -mt-6 bg-[#f4f5f8] px-4 py-4 sm:px-6'>
        <div className='ml-auto w-full max-w-[640px] rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.08)]'>
          <div className='tf-right-drawer-header'>
            <Skeleton className='h-5 w-40' />
          </div>
          <div className='space-y-4 p-4'>
            <Skeleton className='h-48 w-full rounded-2xl' />
            <Skeleton className='h-36 w-full rounded-2xl' />
            <Skeleton className='h-32 w-full rounded-2xl' />
          </div>
        </div>
      </div>
    )
  }

  if (isEdit && (isError || !detail?.data)) {
    const forbidden = isForbiddenError(error)
    return (
      <div className='space-y-4 py-10 text-center'>
        <p className='text-muted-foreground'>
          {forbidden ? t('detail.forbidden') : t('detail.notFound')}
        </p>
        <Button type='button' onClick={() => navigate('/tasks')}>
          {t('detail.backToTasks')}
        </Button>
      </div>
    )
  }

  const body = (
    <>
      <div
        ref={setPortalContainer}
        className={cn(
          'space-y-4 bg-[#f4f5f8] p-4',
          embedded && 'min-h-0 flex-1 overflow-y-auto'
        )}
      >
        <SectionCard index={1} title={t('detail.information')}>
          <div className='space-y-1.5'>
            <RequiredLabel htmlFor='title'>
              {t('form.titleLabel')}
            </RequiredLabel>
            <Input
              id='title'
              value={title}
              maxLength={150}
              placeholder={t('form.titlePlaceholder')}
              onChange={e => setTitle(e.target.value)}
              className={cn(
                fieldInputClass,
                errors.title && 'border-destructive'
              )}
            />
            {errors.title && (
              <p className='text-xs text-destructive'>{errors.title}</p>
            )}
          </div>

          <div className='space-y-1.5'>
            <FieldLabel htmlFor='description'>
              {t('form.description')}
            </FieldLabel>
            <Textarea
              id='description'
              value={description}
              maxLength={2000}
              rows={4}
              placeholder={t('form.descriptionPlaceholder')}
              onChange={e => setDescription(e.target.value)}
              className={textareaFieldClass}
            />
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <RequiredLabel>{t('form.assignedTo')}</RequiredLabel>
              <EntityPicker
                queryKey='task-assignee-users'
                fetcher={p => usersApi.list(p)}
                getOptionLabel={u => u.full_name ?? u.email ?? u.id}
                getOptionValue={u => u.id}
                value={assignedTo}
                initialLabel={assigneeLabel}
                preload
                portalContainer={portalContainer}
                onChange={(value, user) => {
                  setAssignedTo(value)
                  setAssigneeLabel(user.full_name ?? user.email ?? user.id)
                  setErrors(prev => ({ ...prev, assigned_to: undefined }))
                }}
                placeholder={t('form.selectPlaceholder')}
                disabled={assigneeLocked}
                className={cn(
                  entityPickerClass,
                  errors.assigned_to && 'border-destructive'
                )}
              />
              {errors.assigned_to && (
                <p className='text-xs text-destructive'>{errors.assigned_to}</p>
              )}
            </div>

            <div className='space-y-1.5'>
              <RequiredLabel htmlFor='due_date'>
                {t('form.dueDate')}
              </RequiredLabel>
              <DatePicker
                id='due_date'
                value={dueDate}
                onChange={value => {
                  setDueDate(value)
                  setErrors(prev => ({ ...prev, due_date: undefined }))
                }}
                className={dateFieldClass}
                error={!!errors.due_date}
                variant='field'
              />
              {errors.due_date && (
                <p className='text-xs text-destructive'>{errors.due_date}</p>
              )}
            </div>
          </div>

          <div className='space-y-1.5'>
            <FieldLabel>{t('form.priority')}</FieldLabel>
            <div className='flex flex-wrap gap-2'>
              {PRIORITY_OPTIONS.map(option => (
                <PriorityChip
                  key={option.value}
                  label={t(option.labelKey)}
                  priority={option.value}
                  selected={priority === option.value}
                  onClick={() => {
                    setPriority(option.value)
                    setErrors(prev => ({ ...prev, priority: undefined }))
                  }}
                />
              ))}
            </div>
            {errors.priority && (
              <p className='text-xs text-destructive'>{errors.priority}</p>
            )}
          </div>
        </SectionCard>

        <SectionCard index={2} title={t('form.linkedEntity')}>
          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <FieldLabel>{t('form.entityType')}</FieldLabel>
              <Select
                value={relatedType || NONE}
                onValueChange={value => {
                  setRelatedType(value === NONE ? '' : value)
                  setRelatedId(null)
                  setRelatedEntityLabel(undefined)
                }}
              >
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder={t('form.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent container={portalContainer}>
                  <SelectItem value={NONE}>{t('form.none')}</SelectItem>
                  {TASK_RELATED_TYPES.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-1.5'>
              <FieldLabel>{t('form.entity')}</FieldLabel>
              {relatedType && entityFetcher ? (
                <EntityPicker
                  key={relatedType}
                  queryKey={`task-entity-${relatedType}`}
                  fetcher={entityFetcher}
                  getOptionLabel={getRelatedOptionLabel}
                  getOptionValue={item => item.id}
                  value={relatedId}
                  initialLabel={resolvedRelatedEntityLabel}
                  preload
                  portalContainer={portalContainer}
                  onChange={(value, item) => {
                    setRelatedId(value)
                    setRelatedEntityLabel(
                      taskRelatedEntityLabel(relatedType, item)
                    )
                  }}
                  placeholder={t('form.selectPlaceholder')}
                  className={entityPickerClass}
                />
              ) : (
                <div className='flex h-10 items-center rounded-xl border border-transparent bg-[#f4f5f8] px-3 text-sm font-normal text-[#9ca3af]'>
                  {t('form.selectPlaceholder')}
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        <SectionCard index={3} title={t('form.subtasksTitle')}>
          {isEdit && id ? (
            <TaskChecklistSection taskId={id} variant='form' />
          ) : (
            <SubtaskEditor
              items={checklist}
              disabled={pending}
              onAdd={addSubtask}
              onChange={updateSubtask}
              onRemove={removeSubtask}
              addLabel={t('checklist.add')}
              itemLabel={t('form.titleLabel')}
              placeholder={t('checklist.addPlaceholder')}
              removeLabel={t('checklist.remove')}
            />
          )}
        </SectionCard>

        <SectionCard index={4} title={t('form.internalNotes')}>
          {isEdit && id ? (
            <TaskComments
              taskId={id}
              variant='internalNotes'
              textareaClassName='border-transparent'
            />
          ) : (
            <div className='space-y-3'>
              <Textarea
                id='internal_notes'
                value={internalNotes}
                maxLength={2000}
                rows={4}
                placeholder={t('form.internalNotesPlaceholder')}
                onChange={e => setInternalNotes(e.target.value)}
                className={spaciousTextareaFieldClass}
              />
              <p className='text-xs text-[#6c7285]'>
                {t('form.internalNotesHint')}
              </p>
            </div>
          )}
        </SectionCard>

        <SectionCard index={5} title={t('attachments.title')}>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            accept={DOCUMENT_FILE_ACCEPT}
            className='hidden'
            onChange={event => {
              pickFiles(event.target.files)
              event.target.value = ''
            }}
          />

          <FileUploadDropzone
            onClick={() => fileInputRef.current?.click()}
            disabled={pending}
            primaryText={tShared('attachFiles.uploadHintPrimary')}
            secondaryText={tShared('attachFiles.uploadHintFormats')}
            dragActive={isAttachmentDragActive}
            hasError={!!errors.attachments}
            onDragOver={event => {
              event.preventDefault()
              setIsAttachmentDragActive(true)
            }}
            onDragLeave={event => {
              event.preventDefault()
              setIsAttachmentDragActive(false)
            }}
            onDrop={event => {
              event.preventDefault()
              setIsAttachmentDragActive(false)
              pickFiles(event.dataTransfer.files)
            }}
          />

          {errors.attachments && (
            <p className='text-xs text-destructive'>{errors.attachments}</p>
          )}

          <AttachmentPreviewGrid
            items={attachments}
            typeBadgeClassName='font-medium'
            metadataClassName='font-normal leading-4'
            onRemove={item => {
              void removeAttachment(item)
            }}
            onRename={renameAttachment}
            removeLabel={tShared('attachFiles.remove')}
          />
        </SectionCard>
      </div>

      <div className='tf-right-drawer-footer justify-end'>
        <Button
          type='button'
          variant='outline'
          className={outlineBtnClass}
          onClick={onCancel}
          disabled={pending}
        >
          {t('form.cancel')}
        </Button>
        <Button
          type='button'
          className={primaryBtnClass}
          onClick={onSubmit}
          disabled={pending}
        >
          {isEdit ? t('form.saveChanges') : t('form.save')}
        </Button>
      </div>
    </>
  )

  if (embedded) {
    return (
      <div className='flex h-full min-h-0 flex-col bg-[#f4f5f8]'>{body}</div>
    )
  }

  return (
    <div className='-mx-6 -mt-6 bg-[#f4f5f8] px-4 py-4 sm:px-6'>
      <div className='ml-auto flex w-full max-w-[640px] flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.08)]'>
        <header className='tf-right-drawer-header justify-between'>
          <h1 className='font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
            {pageTitle}
          </h1>
          <button
            type='button'
            onClick={onCancel}
            className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#f4f5f8] text-[#5f6673] hover:bg-[#f4f5f8]'
            aria-label={t('form.cancel')}
          >
            <X className='h-5 w-5' />
          </button>
        </header>
        {body}
      </div>
    </div>
  )
}
